import { el } from '../dom';
import { TRAIT_VALUES } from '../../core/traits';
import type { TraitKey } from '../../types';

// A glyph per trait VALUE — thirty-eight of them, which is a different job from
// the nav bar's icons and lives in a different file for that reason.
//
// `icons.ts` argues, correctly, that an icon in a nav bar is wayfinding and that
// a hand-drawn nest does not read as "Library" at 21px. These are not wayfinding.
// They sit next to their own word every time they appear — on the board, in the
// dropdown, on the species page, in the curate table — so nothing rests on being
// recognised cold. What they buy is scanning: a column of thirteen habitats is a
// wall of similar words, and the same column with a tree, a wave and a mountain
// in it can be read down at a glance. The board leans on that hardest, where a
// cell is 70px wide and "invertebrates" does not fit at a readable size.
//
// Same drawing contract as icons.ts, so the two sets sit together: 24×24 field,
// stroke only, round caps and joins, stroke-width 2, currentColor throughout.
//
// SIZE is the one axis drawn as a scale rather than a picture. Length has no
// silhouette that survives 20px — a wren and a heron are the same bird shape —
// so it is five ascending bars, which says "the third of five" without asking
// anyone to judge a drawing's scale.

/**
 * Five bars, of which the first N are lit.
 *
 * Drawn as a full scale with the rest faint rather than as N bars alone: a lone
 * short bar is a speck, and — more to the point — every size then has the same
 * silhouette, so the glyph says "the third of five" at a glance instead of asking
 * anyone to count. The faint bars are `stroke-opacity`, which rides on
 * currentColor and so survives both themes and the inverted green cells.
 */
function sizeBars(lit: number): string {
  let out = '';
  for (let i = 0; i < 5; i++) {
    const x = 4.5 + i * 4;
    const h = 2.5 + i * 2.5;
    const faint = i >= lit ? ' stroke-opacity=".22"' : '';
    out += `<path d="M${x} 19v-${h}"${faint}/>`;
  }
  return out;
}

const SIZE: Record<string, string> = {
  '8-13': sizeBars(1),
  '14-20': sizeBars(2),
  '21-42': sizeBars(3),
  '43-100': sizeBars(4),
  '100+': sizeBars(5),
};

const PREY: Record<string, string> = {
  // A body, a tail and an eye. The tail is what stops it reading as a leaf.
  fish:
    `<path d="M20.5 12c0 3-3.2 5.5-7 5.5S6.5 15 6.5 12s3.2-5.5 7-5.5 7 2.5 7 5.5z"/>` +
    `<path d="M6.5 12 2.5 8v8z"/><path d="M17 10.5h.01"/>`,
  // Cherries. One berry with a leaf kept reading as a digit; two berries on two
  // stalks cannot be read as anything else.
  fruit:
    `<circle cx="7.5" cy="16.5" r="4"/><circle cx="16.5" cy="14.5" r="4"/>` +
    `<path d="M7.5 12.5C7.5 8 10 4.5 15 3.5"/>` +
    `<path d="M16.5 10.5c0-3-.5-5.5-1.5-7"/>`,
  // A bug, for every invertebrate: the beetle stands for the caterpillar, the
  // earthworm, the snail and the crab, the way Wingspan's does. A worm glyph sat
  // beside it while `insect` was a separate value and went with it.
  invertebrate:
    `<ellipse cx="12" cy="13.5" rx="3.5" ry="5.5"/><circle cx="12" cy="6" r="2"/>` +
    `<path d="m10.6 4.6-1.6-2"/><path d="m13.4 4.6 1.6-2"/>` +
    `<path d="M8.6 10.5 5 9"/><path d="M8.5 13.5H4.5"/><path d="m8.6 16.5-3.6 1.5"/>` +
    `<path d="m15.4 10.5 3.6-1.5"/><path d="M15.5 13.5h4"/><path d="m15.4 16.5 3.6 1.5"/>`,
  // A bone, which is what "meat" is a picture of in every icon set there is.
  meat:
    `<path d="m8 16 8-8"/><circle cx="6" cy="18.5" r="2.2"/><circle cx="8.5" cy="20.5" r="2.2"/>` +
    `<circle cx="18" cy="5.5" r="2.2"/><circle cx="15.5" cy="3.5" r="2.2"/>`,
  plant: `<path d="M20 4c0 8.5-5.5 14-14 15C6 10.5 11.5 5 20 4z"/><path d="M7 18 16.5 8"/>`,
  // A grain with its midrib — an oval alone would be an egg.
  seed: `<ellipse cx="12" cy="12" rx="4" ry="7.5"/><path d="M12 4.5v15"/>`,
};

const HABITAT: Record<string, string> = {
  // On the wing. A cloud was the first try and it collided with the shrub at
  // 19px — both are a soft blob — so this is the other half of what "aerial"
  // means: three birds at distance, which nothing else in the set looks like.
  aerial:
    `<path d="M2.5 12.5c2.2-3.2 4.4-3.2 6.6 0"/>` +
    `<path d="M13 7c1.7-2.4 3.4-2.4 5.1 0"/>` +
    `<path d="M11 19c1.7-2.4 3.4-2.4 5.1 0"/>`,
  // Furrows running to a horizon.
  agriculture:
    `<path d="M2 18h20"/><path d="M4.5 18 8 8"/><path d="m10.5 18 1.5-10"/>` +
    `<path d="m16 18-1.5-10"/><path d="M19.5 18 16 8"/>`,
  // A saguaro, drawn wide rather than tall: the arms are the whole silhouette,
  // and a narrow one reads as a trident.
  arid:
    `<path d="M12 21V4"/>` +
    `<path d="M12 15H7.5A3.5 3.5 0 0 1 4 11.5V8"/>` +
    `<path d="M12 12h4.5A3.5 3.5 0 0 0 20 8.5V6"/>`,
  // Water meeting land. Marine is three runs of open swell and nothing else;
  // this is one run of it against a headland, which is the whole difference and
  // has to be drawn as a solid wedge to survive being 19px wide.
  coastal:
    `<path d="M2 19.5h20"/>` +
    `<path d="M2 15c2-1.6 3.6-1.6 5.6 0s3.6 1.6 5.6 0"/>` +
    `<path d="M13 19.5 21.5 9.5v10z"/>`,
  forest: `<path d="M12 3 6.5 11h3L5 17h14l-4.5-6h3z"/><path d="M12 17v4"/>`,
  freshwater: `<path d="M12 3.5c3.7 4.7 5.5 7.8 5.5 10.2a5.5 5.5 0 0 1-11 0c0-2.4 1.8-5.5 5.5-10.2z"/>`,
  // Blades rising from their own points on the ground, not from one. Every
  // version that rooted them together drew an arrowhead or a mound — the
  // convergence is the whole silhouette at 20px, whatever happens above it.
  grassland:
    `<path d="M3 20.5h18"/>` +
    `<path d="M7 20.5c0-4.5.7-7.5 2.2-9.5"/>` +
    `<path d="M12 20.5c0-5.5.5-9 1.6-11"/>` +
    `<path d="M17 20.5c0-4.5-.7-7.5-2.2-9.5"/>`,
  // Open water: three runs of swell and nothing else in the frame.
  marine:
    `<path d="M2 7.5c2.5-2 4-2 6.5 0s4 2 6.5 0 4-2 6.5 0"/>` +
    `<path d="M2 13c2.5-2 4-2 6.5 0s4 2 6.5 0 4-2 6.5 0"/>` +
    `<path d="M2 18.5c2.5-2 4-2 6.5 0s4 2 6.5 0 4-2 6.5 0"/>`,
  mountain: `<path d="m2.5 19 6.5-11 4 6.5 2.5-4L21.5 19z"/>`,
  // Three overlapping crowns on a stem. A single canopy is a lollipop however it
  // is shaped; what says "shrub" is more than one of them.
  scrubland:
    `<path d="M3 20.5h18"/><path d="M12 20.5v-4.5"/>` +
    `<circle cx="7.8" cy="12.5" r="3.8"/><circle cx="16.2" cy="12.5" r="3.8"/>` +
    `<circle cx="12" cy="9" r="4"/>`,
  // Four axes through one centre, and nothing else. The first version hung
  // chevrons off two of the six arms and only two, which is a snowflake that has
  // been dropped.
  tundra:
    `<path d="M12 3v18"/><path d="M3 12h18"/>` +
    `<path d="m5.6 5.6 12.8 12.8"/><path d="m18.4 5.6-12.8 12.8"/>`,
  urban:
    `<path d="M2.5 21V9.5l6-3.5V21"/><path d="M8.5 21v-9l6-2.5V21"/>` +
    `<path d="M14.5 21v-8l7 2.5V21"/><path d="M5 12h1"/><path d="M11 15h1"/>`,
  // Cattails. The one habitat with a plant that means only this.
  wetland:
    `<path d="M3 21h18"/><path d="M8 21V10.5"/><rect x="6.5" y="4" width="3" height="6" rx="1.5"/>` +
    `<path d="M15.5 21v-7.5"/><rect x="14" y="8" width="3" height="5.5" rx="1.5"/>`,
};

// Four feet, drawn from the same standing leg so the difference between them is
// the only thing that moves: hooks, a grip, webbing, or bare toes.
const FEET: Record<string, string> = {
  hunting:
    `<path d="M12 3v8"/>` +
    `<path d="M12 11c-3.5 0-5.5 2.5-5.5 5.5"/><path d="M6.5 16.5c-1 .8-2 1-3 .8"/>` +
    `<path d="M12 11c3.5 0 5.5 2.5 5.5 5.5"/><path d="M17.5 16.5c1 .8 2 1 3 .8"/>` +
    `<path d="M12 11v6.5"/><path d="M12 17.5c-.4 1-1 1.6-1.8 2"/>`,
  perching:
    `<path d="M3 15h18"/><path d="M12 5v10"/>` +
    `<path d="M12 15c-2.6 0-4.2 1.6-4.2 3.8"/><path d="M12 15c2.6 0 4.2 1.6 4.2 3.8"/>` +
    `<path d="M12 15c-.6 1.4-1.8 2.2-3.4 2.4"/>`,
  swimming:
    `<path d="M12 3v7"/><path d="m12 10-6.5 9"/><path d="M12 10v9"/><path d="m12 10 6.5 9"/>` +
    `<path d="M5.5 19c2.2-1.3 4.3-1.9 6.5-1.9s4.3.6 6.5 1.9"/>`,
  walking:
    `<path d="M12 12v9"/><path d="m12 12-6.5-5"/><path d="M12 12V3.5"/>` +
    `<path d="m12 12 6.5-5"/>`,
};

const NEST: Record<string, string> = {
  // A hole going down, in flat ground, with the tunnel behind it. A mound with an
  // arch in it — which is what this was — is indistinguishable from the dome and
  // the mound at 19px, and this set had three of those.
  burrow:
    `<path d="M2 10h20"/><ellipse cx="9" cy="10" rx="4" ry="2.2"/>` +
    `<path d="M9 12.2c.4 4.5 3.2 7.3 8 8.3"/>`,
  cavity: `<path d="M6 2.5v19"/><path d="M18 2.5v19"/><circle cx="12" cy="11" r="3.5"/>`,
  cup: `<path d="M3 10h18"/><path d="M4.5 10c0 4.4 3.4 8 7.5 8s7.5-3.6 7.5-8"/>`,
  // A woven ball with the entrance in its side — a whole sphere, not a half one,
  // which is what separates it from the mound it sits on.
  dome:
    `<path d="M2.5 21h19"/><circle cx="12" cy="12" r="7.5"/>` +
    `<circle cx="16.2" cy="14.5" r="2.1"/>`,
  // A heap, drawn in courses so it reads as something piled up rather than as a
  // hill with an animal in it.
  mound:
    `<path d="M2 20h20"/><path d="M3.5 20c1.2-5.5 4.2-8.5 8.5-8.5s7.3 3 8.5 8.5"/>` +
    `<path d="M7 20c.7-3 2.4-4.5 5-4.5s4.3 1.5 5 4.5"/>` +
    `<path d="M9.5 15.8c.6-1.6 1.4-2.4 2.5-2.4s1.9.8 2.5 2.4"/>`,
  // No nest of its own: the cup, struck out.
  parasitic:
    `<path d="M3.5 9.5h17"/><path d="M5 9.5c0 4 3.1 7.5 7 7.5s7-3.5 7-7.5"/>` +
    `<path d="M3 21 21 3"/>`,
  pendant:
    `<path d="M3 4.5h18"/><path d="M12 4.5v3"/>` +
    `<path d="M12 7.5c-3.4 0-5.5 3-5.5 6.5S9 21 12 21s5.5-3.5 5.5-7-2.1-6.5-5.5-6.5z"/>` +
    `<circle cx="12" cy="12.5" r="1.4"/>`,
  platform:
    `<path d="M3 15h18"/><path d="M5 11h14"/><path d="M6.5 19h11"/>` +
    `<path d="m8 11-1.5 8"/><path d="m16 11 1.5 8"/>`,
  scrape:
    `<path d="M2 14.5c2.5 3.2 5.7 4.8 10 4.8s7.5-1.6 10-4.8"/>` +
    `<ellipse cx="9.5" cy="13" rx="2" ry="2.6"/><ellipse cx="14.5" cy="13" rx="2" ry="2.6"/>`,
};

const PATHS: Record<TraitKey, Record<string, string>> = {
  size: SIZE,
  prey: PREY,
  habitat: HABITAT,
  feet: FEET,
  nest: NEST,
};

/** Every vocabulary value has a glyph — the tests hold this, so lookups can't miss. */
export function hasTraitIcon(key: TraitKey, value: string): boolean {
  return !!PATHS[key]?.[value];
}

export interface TraitIconOpts {
  /** Rendered pixel size. Default 18. */
  size?: number;
  /**
   * Announce it instead of hiding it.
   *
   * Off by default, and that is the right default everywhere it is used today:
   * the glyph always sits beside its own word, so announcing it would read every
   * value twice.
   */
  label?: string;
}

/** The glyph for one trait value, or nothing for a value with none. */
export function traitIcon(
  key: TraitKey,
  value: string,
  opts: TraitIconOpts = {},
): HTMLElement | null {
  const inner = PATHS[key]?.[value];
  if (!inner) return null;
  const s = opts.size ?? 18;
  return el('span.icon.tico', {
    'aria-hidden': opts.label ? null : 'true',
    role: opts.label ? 'img' : null,
    'aria-label': opts.label ?? null,
    html:
      `<svg viewBox="0 0 24 24" width="${s}" height="${s}" fill="none" ` +
      `stroke="currentColor" stroke-width="2" stroke-linecap="round" ` +
      `stroke-linejoin="round">${inner}</svg>`,
  });
}

/** Which vocabulary values are missing a glyph — for the test that forbids any. */
export function missingTraitIcons(): string[] {
  const gaps: string[] = [];
  for (const key of Object.keys(PATHS) as TraitKey[]) {
    for (const value of TRAIT_VALUES[key]) {
      if (!PATHS[key][value]) gaps.push(`${key}:${value}`);
    }
    for (const value of Object.keys(PATHS[key])) {
      if (!TRAIT_VALUES[key].includes(value)) gaps.push(`${key}:${value} (no such value)`);
    }
  }
  return gaps.sort();
}
